import {
  allRequest,
  deleteRequest,
  putRequest,
  REQUEST_METHOD_GET
} from "../../../middlewares/api/actions";
import {
  getCategorySchemeUrl,
  getDataflowUrl,
  getDeleteArtefactUrl,
  getUpdateArtefactsUrl
} from "../../../constants/urls";
import _ from "lodash";
import {
  getArtefactTripletFromUrn,
  getSdmxJsonFromSdmxJsonStructures,
  getSdmxJsonStructureFromArtefact,
  getSdmxJsonStructureFromItem,
  SDMX_JSON_CATEGORISATION_LIST_KEY,
  SDMX_JSON_CATEGORY_SCHEME_LIST_KEY
} from "../../../utils/sdmxJson";
import {Modal} from "antd";
import { getMappedTree } from "../../../utils/tree";

export const CATEGORY_SCHEMES_AND_DATAFLOWS_TREE_READ = 'CATEGORY_SCHEMES_AND_DATAFLOWS_TREE_READ';
export const CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISE = 'CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISE';
export const CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISATION_DELETE = 'CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISATION_DELETE';
export const CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_LANGUAGE_CHANGE = 'CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_LANGUAGE_CHANGE';
export const CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_TO_CATEGORIZE = 'CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_TO_CATEGORIZE';
export const CATEGORY_SCHEMES_AND_DATAFLOWS_REORDER_CATEGORY_SCHEMES = 'CATEGORY_SCHEMES_AND_DATAFLOWS_REORDER_CATEGORY_SCHEMES';
export const CATEGORY_SCHEMES_AND_DATAFLOWS_UPDATE_ORDER_CATEGORY_SCHEMES = 'CATEGORY_SCHEMES_AND_DATAFLOWS_UPDATE_ORDER_CATEGORY_SCHEMES';

export const readCategorySchemesAndDataflowsTree = (lang, categorySchemePermissions, username) => ({
  ...allRequest(
    CATEGORY_SCHEMES_AND_DATAFLOWS_TREE_READ,
    [REQUEST_METHOD_GET, REQUEST_METHOD_GET],
    [getCategorySchemeUrl(null, true), getDataflowUrl()]
  ),
  lang,
  categorySchemePermissions,
  username
});

export const categoriseCategorySchemesAndDataflowsDataflow = (
  dataflow,
  newCategoryUrn,
  categorisations,
  to,
  fromSisterDfOrderedCategorisations, 
  toSisterDfOrderedCategorisations,
  lang,
  orderAnnotationType,
  agencyPermissions,
  t
) => {

  if (!dataflow.categoryUrn) {

    // dataflow was uncategorised

    return addCategorisationToDataflow(
      dataflow,
      newCategoryUrn,
      orderAnnotationType,
      lang,
      to,
      toSisterDfOrderedCategorisations,
      agencyPermissions,
      t);
  } else {

    // dataflow was categorised

    return categoriseDataflow(
      dataflow,
      newCategoryUrn,
      categorisations,
      orderAnnotationType,
      lang,
      to,
      fromSisterDfOrderedCategorisations,
      toSisterDfOrderedCategorisations,
      agencyPermissions,
      t
    );
  }
};


const categoriseDataflow = (
  dataflow,
  newCategoryUrn,
  categorisations,
  orderAnnotationType,
  lang,
  to,
  fromSisterDfOrderedCategorisations,
  toSisterDfOrderedCategorisations,
  agencyPermissions,
  t
) => {

  const newCategorisation =
      _.find(categorisations, cat => cat.source === dataflow.urn && cat.target === dataflow.categoryUrn);
    newCategorisation.target = newCategoryUrn;

    newCategorisation.autoAnnotations = [
      ...(newCategorisation.autoAnnotations || []).filter((ann) => (ann && ann.type !== orderAnnotationType)),
      {
        type: orderAnnotationType,
        text: {
          ...((newCategorisation.autoAnnotations || []).find((ann) => (ann && ann.type === orderAnnotationType)) || {}).text || {},
          [lang]: String(to)
        }
      }
    ];

    // sister source df categorisations must have order in 0..N-1

    const newFromSisterDfCategorisations =
      fromSisterDfOrderedCategorisations
        .filter(({source}) => source !== dataflow.urn)
        .map((categoris, index) => {
          const newOrderAnnot = {
            type: orderAnnotationType,
            text: {
              ...((categoris.autoAnnotations || []).find((ann) => (ann && ann.type === orderAnnotationType)) || {}).text || {},
              [lang]: String(index)
            }
          };
          return {
            ...categoris,
            autoAnnotations: [
              ...(categoris.autoAnnotations || []).filter((ann) => (ann && ann.type !== orderAnnotationType)),
              newOrderAnnot
            ]
          };
        });

    // sister target df categorisations must have order in 0..to-1 or to+1..N

    const newToSisterDfCategorisations =
      toSisterDfOrderedCategorisations
        .filter(({source}) => source !== dataflow.urn)
        .map((categoris, index) => {
          const newOrderAnnot = {
            type: orderAnnotationType,
            text: {
              ...((categoris.autoAnnotations || []).find((ann) => (ann && ann.type === orderAnnotationType)) || {}).text || {},
              [lang]: String(index < to ? index : (index + 1))
            }
          };
          return {
            ...categoris,
            autoAnnotations: [
              ...(categoris.autoAnnotations || []).filter((ann) => (ann && ann.type !== orderAnnotationType)),
              newOrderAnnot
            ]
          };
        });

    const structures = [
      getSdmxJsonStructureFromArtefact(newCategorisation),
      ...newFromSisterDfCategorisations.map(getSdmxJsonStructureFromArtefact),
      ...newToSisterDfCategorisations.map(getSdmxJsonStructureFromArtefact)
    ];

    let notAllowedAgency = null;

    structures.forEach(({agencyID}) => {
      if (!notAllowedAgency && !agencyPermissions.includes(agencyID)) {
        notAllowedAgency = agencyID;
      }
    });

    if (notAllowedAgency) {

      Modal.error({title: t("scenes.metaManager.categorySchemesDataflowsTree.errors.notAllowedAgency.title", {agencyId: notAllowedAgency})});

      return ({type: "NOP"});
    }


    return putRequest(
      CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISE,
      getUpdateArtefactsUrl(),
      getSdmxJsonFromSdmxJsonStructures(
        structures,
        SDMX_JSON_CATEGORISATION_LIST_KEY
      ),
      t => ({
        success: t('scenes.metaManager.categorySchemesAndDataflows.messages.categorisation.update.success')
      })
    )
}

export const addCategorisationToDataflow = (
  dataflow,
  newCategoryUrn,
  orderAnnotationType,
  lang,
  to,
  toSisterDfOrderedCategorisations,
  agencyPermissions,
  t
) => {

  const newCategorisationId = 'CAT_' +
  dataflow.id.substr(0, 40) +
  '_' +
  String(_.random(99999)).padStart(5, '0');

  const newCategorisation = {
    id: newCategorisationId,
    agencyID: dataflow.agencyID,
    version: dataflow.version,
    name: {
      en: newCategorisationId
    },
    source: dataflow.urn,
    target: newCategoryUrn,
    autoAnnotations: [
      {
        type: orderAnnotationType,
        text: {[lang]: String(to)}
      }
    ]
  };

  const newToSisterDfCategorisations =
      toSisterDfOrderedCategorisations
        .filter(({source}) => source !== dataflow.urn)
        .map((categoris, index) => {
          const newOrderAnnot = {
            type: orderAnnotationType,
            text: {
              ...((categoris.autoAnnotations || []).find((ann) => (ann && ann.type === orderAnnotationType)) || {}).text || {},
              [lang]: String(index < to ? index : (index + 1))
            }
          };
          return {
            ...categoris,
            autoAnnotations: [
              ...(categoris.autoAnnotations || []).filter((ann) => (ann && ann.type !== orderAnnotationType)),
              newOrderAnnot
            ]
          };
        });

  const structures = [
    getSdmxJsonStructureFromArtefact(newCategorisation),
    ...newToSisterDfCategorisations.map(getSdmxJsonStructureFromArtefact)
  ];
    
  let notAllowedAgency = null;

  structures.forEach(({agencyID}) => {
    if (!notAllowedAgency && !agencyPermissions.includes(agencyID)) {
      notAllowedAgency = agencyID;
    }
  });
    
  if (notAllowedAgency) {
    Modal.error({title: t("scenes.metaManager.categorySchemesDataflowsTree.errors.notAllowedAgency.title", {agencyId: notAllowedAgency})});
    return ({type: "NOP"});
  }

  return putRequest(
    CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISE,
    getUpdateArtefactsUrl(),
    getSdmxJsonFromSdmxJsonStructures(
      structures,
      SDMX_JSON_CATEGORISATION_LIST_KEY
    ),
    t => ({
      success: t('scenes.metaManager.categorySchemesAndDataflows.messages.categorisation.update.success')
    })
  );

}

export const deleteCategorySchemesAndDataflowsDataflowCategorisation = (dataflow, categorisations) => {
  const categorisation =
    _.find(categorisations, cat => cat.source === dataflow.urn && cat.target === dataflow.categoryUrn);

  return deleteRequest(
    CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_CATEGORISATION_DELETE,
    getDeleteArtefactUrl("Categorisation", getArtefactTripletFromUrn(categorisation.urn)),
    t => ({
      success: t('scenes.metaManager.categorySchemesAndDataflows.messages.categorisation.delete.success')
    })
  )
};

export const changeCategorySchemesAndDataflowsLanguage = () => ({
  type: CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_LANGUAGE_CHANGE,
})

export const setToCategorizeDataflow = dataflow => ({
  type: CATEGORY_SCHEMES_AND_DATAFLOWS_DATAFLOW_TO_CATEGORIZE,
  dataflow
});

export const reorderCategoryScheme = tree => ({
  type: CATEGORY_SCHEMES_AND_DATAFLOWS_REORDER_CATEGORY_SCHEMES,
  tree
})

export const updateOrderCategoryScheme = (tree) => {
  return ({
    ...putRequest(
      CATEGORY_SCHEMES_AND_DATAFLOWS_UPDATE_ORDER_CATEGORY_SCHEMES,
      getUpdateArtefactsUrl(),
      getSdmxJsonFromSdmxJsonStructures(
        tree,
        SDMX_JSON_CATEGORY_SCHEME_LIST_KEY
      ),
      t => ({
        success: t('reduxComponents.categorySchemeDetail.messages.update.success')
      })
    )
  });
}